import Promise from 'bluebird';
import React, { useContext } from 'react';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import type {
  I18nModel,
  WithI18nOuterProps,
  WithI18nProps,
} from '@bingads-webui-react/i18n-context';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import globalApps from '@bingads-webui-clientcenter/global-apps';
import type { GlobalAppsI18n } from '@bingads-webui-clientcenter/global-apps';

export interface MergeableI18nModel extends I18nModel {
  mergeFrom(parentI18n: I18nModel): void;
}

export type I18nInjectedProps = WithI18nProps;

export interface DynamicImportModule<T> {
  default: T;
}

export type DynamicImportFactory<T> = () => T | PromiseLike<T>;

export type WithMergedI18nProps<P extends I18nInjectedProps> =
  WithI18nOuterProps<P>;

export interface LoadPageWebpackOptions<P extends I18nInjectedProps> {
  importFeature?: DynamicImportFactory<React.ComponentType<P>>;
  importL10n?: DynamicImportFactory<DynamicImportModule<MergeableI18nModel>>;
}

export const mergeI18n = (childI18n: MergeableI18nModel | undefined, parentI18n: I18nModel) => {
  if (childI18n && parentI18n) {
    childI18n.mergeFrom(parentI18n);

    return childI18n;
  }
  return parentI18n;
};

/* This is to merge the parent and child i18n(App and Feature) */
export const withMergedI18n = <P extends I18nInjectedProps>(
  Component: React.ComponentType<P>,
  childI18n: MergeableI18nModel | undefined,
  setGlobalAppsI18n = false
) => {
  const HoC = (props: WithMergedI18nProps<P>) => {
    const parentI18n = useContext(I18nContext) || props.i18n;
    const i18n = mergeI18n(childI18n, parentI18n);
    if (setGlobalAppsI18n) {
      globalApps.i18n = i18n as I18nModel & GlobalAppsI18n;
    }
    return (
      <I18nContext.Provider value={i18n}>
        <Component {...props as P} i18n={i18n} />
      </I18nContext.Provider>);
  };

  HoC.displayName = `WithI18n(${getDisplayName(Component)})`;

  return HoC;
};

export function loadPageWebpack<P extends I18nInjectedProps>({
  importFeature = _.noop as () => React.ComponentType<P>,
  importL10n = _.noop as () => DynamicImportModule<MergeableI18nModel>,
}: LoadPageWebpackOptions<P>) {
  return Promise.all([
    importL10n(),
    importFeature(),
  ]).then(([{ default: i18n } = {}, Feature]) => ({ default: withMergedI18n(Feature, i18n, true) }));
}

export const getDefaultExport = <T, >(e: DynamicImportModule<T>) => e.default;
